import React, { useEffect, useState } from 'react';
import { getCrmVariables, uploadCrmVariableCsv } from '../config/api';
import { saveBlob } from '../utils/nativeFiles';

const ResultGroup = ({ title, rows, className }) => (
  <div className={`crm-variable-import-result ${className || ''}`}>
    <strong>{title}: {rows.length}</strong>
    {rows.length > 0 && (
      <div className="crm-variable-import-rows">
        {rows.map((row) => (
          <div key={`${title}-${row.row}`}>
            Row {row.row} · {row.externalKey}
            {row.leadIds?.length ? ` · Lead ${row.leadIds.join(', ')}` : ''}
          </div>
        ))}
      </div>
    )}
  </div>
);

const CrmVariableCsvImportDialog = ({ onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [variables, setVariables] = useState([]);

  useEffect(() => {
    getCrmVariables()
      .then((response) => setVariables((response?.data || response)?.variables || []))
      .catch(() => setVariables([]));
  }, []);

  const downloadSample = () => {
    const csvCell = (value) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;
    const sampleValue = (variable) => {
      if (variable.type === 'number') return '100';
      if (variable.type === 'date') return new Date().toISOString().slice(0, 10);
      if (variable.type === 'list') return variable.allowedValues?.[0] || '';
      return 'Sample value';
    };
    const headers = ['pmsg_vendor_id', ...variables.map((variable) => variable.key)];
    const values = ['PMSG_VENDOR_ID_HERE', ...variables.map(sampleValue)];
    const csv = `\uFEFF${headers.map(csvCell).join(',')}\r\n${values.map(csvCell).join(',')}\r\n`;
    saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'crm-variable-import-sample.csv');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!file) return setError('Select a CSV file.');
    setUploading(true);
    setError('');
    setResult(null);
    try {
      const response = await uploadCrmVariableCsv(file);
      setResult(response?.data || response);
      if (onImported) await onImported(response?.data || response);
    } catch (err) {
      setError(err.message || 'Could not upload variable CSV.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={uploading ? undefined : onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()} style={{ maxWidth: '680px' }}>
        <form onSubmit={submit}>
          <div className="modal-header">
            <h3>Import Variable Values</h3>
            <button type="button" className="modal-close" onClick={onClose} disabled={uploading}>×</button>
          </div>
          <div className="modal-body">
            <p className="crm-muted">
              Use one external key column: <strong>pmsg_vendor_id</strong>, <strong>phone</strong>, or <strong>gst</strong>.
              Add one or more active variable keys as the remaining column headers.
            </p>
            <div className="crm-form-help">Example: phone, annual_spend, follow_up_month</div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={downloadSample}
              disabled={uploading || variables.length === 0}
              style={{ marginTop: '10px' }}
            >
              Download sample CSV
            </button>
            {variables.length === 0 && (
              <div className="crm-form-help">Create at least one active variable before downloading a sample.</div>
            )}
            <div className="form-group">
              <label className="form-label" htmlFor="crm-variable-csv">CSV file</label>
              <input
                id="crm-variable-csv"
                className="form-control"
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => { setFile(event.target.files?.[0] || null); setError(''); setResult(null); }}
                disabled={uploading}
              />
            </div>
            {error && <div className="crm-form-error">{error}</div>}
            {result && (
              <div className="crm-variable-import-summary">
                <ResultGroup title="Matched" rows={result.matched || []} className="matched" />
                <ResultGroup title="Not matched" rows={result.notMatched || []} className="not-matched" />
                <ResultGroup title="Ambiguous" rows={result.ambiguous || []} className="ambiguous" />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={uploading}>{result ? 'Close' : 'Cancel'}</button>
            <button type="submit" className="btn btn-primary" disabled={uploading || !file}>{uploading ? 'Uploading…' : 'Upload CSV'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmVariableCsvImportDialog;
